import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/lib/articles";
import { getCategoryById } from "@/lib/articles";

interface HeroCarouselProps {
  items: Article[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      if (!items.length) return;
      setCurrent(((next % items.length) + items.length) % items.length);
    },
    [items.length]
  );

  const startAuto = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % (items.length || 1));
    }, 5000);
  }, [items.length]);

  useEffect(() => {
    startAuto();
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [startAuto]);

  const handleManual = (next: number) => {
    go(next);
    startAuto();
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      handleManual(delta < 0 ? current + 1 : current - 1);
    }
    touchStartX.current = null;
  };

  if (!items.length) return null;

  return (
    <section
      className="relative w-full overflow-hidden rounded-lg bg-secondary-900"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点内容轮播"
    >
      <div className="relative h-[420px] md:h-[560px] w-full">
        {items.map((article, index) => {
          const category = getCategoryById(article.categoryId);
          const active = index === current;
          return (
            <div
              key={article.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                active ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
            >
              <img
                src={article.cover}
                alt={article.title}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/20"></div>
              <div className="absolute inset-0 flex items-end">
                <div className="w-full px-5 pb-10 md:px-14 md:pb-14">
                  <div className="max-w-2xl">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                      <i className="ri-fire-line w-4 h-4 flex items-center justify-center"></i>
                      焦点推荐 · {category?.name}
                    </span>
                    <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-white md:text-4xl">
                      {article.title}
                    </h2>
                    <p className="mt-3 line-clamp-2 max-w-xl text-sm text-white/80 md:text-base">
                      {article.summary}
                    </p>
                    <Link
                      to={`/detail/${article.id}`}
                      className="mt-6 inline-flex items-center gap-2 rounded-full bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-500 hover:text-background-50 cursor-pointer"
                    >
                      阅读全文
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => handleManual(current - 1)}
        className="absolute left-3 top-1/2 flex h-9 w-9 md:h-11 md:w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur transition-colors hover:bg-primary-500 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => handleManual(current + 1)}
        className="absolute right-3 top-1/2 flex h-9 w-9 md:h-11 md:w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur transition-colors hover:bg-primary-500 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 right-5 md:right-14 flex items-center gap-2">
        {items.map((article, index) => (
          <button
            key={article.id}
            type="button"
            onClick={() => handleManual(index)}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              index === current ? "w-6 bg-primary-500" : "w-2 bg-white/60 hover:bg-white"
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          ></button>
        ))}
      </div>
    </section>
  );
}